import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import moment, { Moment } from 'moment'
import { useTranslation } from 'next-i18next'
import { parseCookies } from 'nookies'
import { useRouter } from 'next/router'
import debounce from 'lodash.debounce'

import useCheckBreakpoint from 'lib/checkBreakpoint'
import eventEmitter from 'lib/eventEmitter'

import { SpaceBookingDatePicker } from 'components/Pages/Pudo/SpaceBookingDatePicker'
import { DuplicateReserveModal } from 'components/modals/DuplicateReserveModal'

import { useReservationPrice } from 'features/space/hooks/useReservationPrice'
import { SpaceCurrency } from 'features/space/types/spaceDetail'
import type { Space } from 'features/space/types/spaceDetail'

import { SizePickingSection } from './parts/SizePickingSection'
import { PricingSection } from './parts/PricingSection'
import { BookingActionSection } from './parts/BookingActionSection'
import styles from './styles.module.css'

export type SpaceBookingParams = {
  /** 'YYYY-MM-DD HH:mm' */
  startDate: string
  /** 'YYYY-MM-DD HH:mm' */
  endDate: string
  reservationItems: { [key: string]: number }
  /** POST /api/web/reservations/price の見積もり結果 (minor units) */
  price: number | null
  /** ISO 通貨コード (例: JPY) */
  currency: string | null
}

type Props = {
  isLoading: boolean
  space: Space
  onBooking: (params: SpaceBookingParams) => void
}

export const SPACE_BOOKING_SESSION_STORAGE_KEY = 'spaceBookingInfo'

const parseStoredJson = (raw: string | null) => {
  if (!raw) return null

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/** space/[id] の BookingColumnComponent と同様に search_params から日時の初期値を決める */
const getInitialDatesFromSearchParams = (
  selectedDate: { startDate?: string | null; endDate?: string | null } | undefined
) => ({
  startDate: selectedDate?.startDate ? moment(selectedDate.startDate) : moment(),
  endDate: selectedDate?.endDate ? moment(selectedDate.endDate) : moment(),
})

const getInitialTimesFromSearchParams = (
  selectedDateTime: { startDateTime?: string; endDateTime?: string } | undefined
) => ({
  startTime: selectedDateTime?.startDateTime || '',
  endTime: selectedDateTime?.endDateTime || '',
})

/**
 * /spaces/[uuid] の予約パネル。components/Pages/Pudo/SpaceBooking をベースに、
 * 料金表示を POST /api/web/reservations/price に置き換えて全 space_type に対応。
 */
export const SpaceBookingPanel: FC<Props> = ({ isLoading, space, onBooking }) => {
  const { t } = useTranslation()
  const cookies = parseCookies()
  const router = useRouter()
  const { isMobile } = useCheckBreakpoint()

  const [startDate, setStartDate] = useState<Moment>(null)
  const [startTime, setStartTime] = useState('')
  const [endDate, setEndDate] = useState<Moment>(null)
  const [endTime, setEndTime] = useState('')

  const [reservationItems, setReservationItems] = useState(null)
  const [isDuplicateModalVisible, setDuplicateModalVisible] = useState(false)

  const {
    priceInfo,
    isLoading: isPriceLoading,
    isError: isPriceError,
    fetchPrice,
    resetPrice,
  } = useReservationPrice()

  const timeRange = useMemo(() => {
    if (!startDate || !endDate) return null

    const checkinDayOfWeek = startDate.day()
    const checkoutDayOfWeek = endDate.day()

    const checkinWorkingDay = space.business_hours.find(
      (day) => day.day_of_week === checkinDayOfWeek
    )
    const checkoutWorkingDay = space.business_hours.find(
      (day) => day.day_of_week === checkoutDayOfWeek
    )

    if (!checkinWorkingDay || !checkoutWorkingDay) return null

    const checkinMinTime = checkinWorkingDay.is_24_hours ? '00:00' : checkinWorkingDay.open_at
    const checkinMaxTime = checkinWorkingDay.is_24_hours ? '23:30' : checkinWorkingDay.close_at

    const checkoutMinTime = checkoutWorkingDay.is_24_hours ? '00:00' : checkoutWorkingDay.open_at
    const checkoutMaxTime = checkoutWorkingDay.is_24_hours ? '23:30' : checkoutWorkingDay.close_at

    return {
      checkin: {
        minTime: checkinMinTime,
        maxTime: checkinMaxTime,
      },
      checkout: {
        minTime: checkoutMinTime,
        maxTime: checkoutMaxTime,
      },
    }
  }, [startDate, endDate, space.business_hours])

  const isAuthorized = useMemo(() => {
    return !!cookies?.jwt_token
  }, [cookies])

  const timePickerError = useMemo(() => {
    if (!startTime || !endTime || !startDate || !endDate) {
      return ''
    }

    const checkin = moment(`${startDate.format('YYYY-MM-DD')} ${startTime}`)
    const checkout = moment(`${endDate.format('YYYY-MM-DD')} ${endTime}`)

    // pudo は現行どおり「3時間以内のチェックイン」の制約を維持する
    if (space.space_type === 'pudo') {
      const maxCheckinTime = moment().add(3, 'hours')
      const isValidCheckinTime = checkin.isBefore(maxCheckinTime)

      if (!isValidCheckinTime) {
        return t('BOOKING.ERRORS.CHECKIN_TIME_MUST_BE_AFTER_CURRENT_TIME_3HOURS')
      }
    }

    const isValidCheckoutTime = checkout.isAfter(checkin)

    if (!isValidCheckoutTime) {
      return t('BOOKING.ERRORS.INVALID_DATES')
    }

    return ''
  }, [startTime, endTime, startDate, endDate, space.space_type, t])

  const isBookingTimeValid = useMemo(() => {
    if (!startTime || !endTime || !startDate || !endDate) return false
    if (timePickerError) return false

    if (space.space_type !== 'pudo') {
      // pudo 以外は現行の /space・/locker と同じく「現在時刻より後のチェックイン」を要求する
      const checkin = moment(`${startDate.format('YYYY-MM-DD')} ${startTime}`)
      return moment().isBefore(checkin)
    }

    return true
  }, [startTime, endTime, startDate, endDate, timePickerError, space.space_type])

  const totalItemCount = useMemo(() => {
    if (!reservationItems) return 0

    return Object.keys(reservationItems).reduce(
      (sum, size) => sum + (Number(reservationItems[size]) || 0),
      0
    )
  }, [reservationItems])

  const debouncedFetchPrice = useMemo(() => debounce(fetchPrice, 500), [fetchPrice])

  useEffect(() => {
    return () => {
      debouncedFetchPrice.cancel()
    }
  }, [debouncedFetchPrice])

  useEffect(() => {
    if (
      !startDate ||
      !endDate ||
      !startTime ||
      !endTime ||
      !totalItemCount ||
      !isBookingTimeValid
    ) {
      debouncedFetchPrice.cancel()
      resetPrice()
      return
    }

    debouncedFetchPrice({
      space_id: space.space_id,
      from: startDate.format('YYYY-MM-DD') + ' ' + startTime,
      to: endDate.format('YYYY-MM-DD') + ' ' + endTime,
      reservation_items: reservationItems,
    })
  }, [
    startDate,
    endDate,
    startTime,
    endTime,
    reservationItems,
    totalItemCount,
    isBookingTimeValid,
    debouncedFetchPrice,
    resetPrice,
    space.space_id,
  ])

  const total = useMemo(() => {
    if (!priceInfo) return null

    return priceInfo.price.toLocaleString()
  }, [priceInfo])

  const currencySymbol = useMemo(() => {
    const currencyKey = priceInfo?.currency?.toLowerCase()

    return SpaceCurrency[currencyKey as keyof typeof SpaceCurrency] || '¥'
  }, [priceInfo])

  const saveBookingInfo = useCallback(() => {
    const bookingInfo = {
      startDate,
      endDate,
      startTime,
      endTime,
      reservationItems,
      spaceId: space.space_id,
    }

    sessionStorage.setItem(SPACE_BOOKING_SESSION_STORAGE_KEY, JSON.stringify(bookingInfo))
  }, [startDate, endDate, startTime, endTime, reservationItems, space.space_id])

  const handleRedirectLogin = useCallback(() => {
    saveBookingInfo()
    router.push('/users/sign_in')
  }, [saveBookingInfo, router])

  useEffect(() => {
    eventEmitter.on('redirectToAuth', saveBookingInfo)

    return () => {
      eventEmitter.removeListener('redirectToAuth', saveBookingInfo)
    }
  }, [saveBookingInfo])

  useEffect(() => {
    const sessionStorageParams = parseStoredJson(
      sessionStorage.getItem(SPACE_BOOKING_SESSION_STORAGE_KEY)
    )

    if (sessionStorageParams) {
      const { startDate, endDate, startTime, endTime, reservationItems } = sessionStorageParams
      !!startDate && setStartDate(moment(startDate))
      !!endDate && setEndDate(moment(endDate))
      !!startTime && setStartTime(startTime)
      !!endTime && setEndTime(endTime)
      setReservationItems(reservationItems)

      sessionStorage.removeItem(SPACE_BOOKING_SESSION_STORAGE_KEY)
      return
    }

    const localStorageParams = parseStoredJson(localStorage.getItem('search_params'))
    const { startDate: initialStartDate, endDate: initialEndDate } =
      getInitialDatesFromSearchParams(localStorageParams?.selectedDate)
    const { startTime: initialStartTime, endTime: initialEndTime } =
      getInitialTimesFromSearchParams(localStorageParams?.selectedDateTime)

    setStartDate(initialStartDate)
    setEndDate(initialEndDate)
    setStartTime(initialStartTime)
    setEndTime(initialEndTime)

    const items = {}

    space.capacity.forEach((capacity) => {
      if (capacity.size === 'small' && localStorageParams?.bagSize) {
        items[capacity.size] = localStorageParams.bagSize
      } else if (capacity.size === 'large' && localStorageParams?.suitcaseSize) {
        items[capacity.size] = localStorageParams.suitcaseSize
      } else {
        items[capacity.size] = 0
      }
    })

    setReservationItems(items)
  }, [space])

  const buildBookingParams = useCallback((): SpaceBookingParams => {
    return {
      startDate: moment(startDate).format('YYYY-MM-DD') + ' ' + startTime,
      endDate: moment(endDate).format('YYYY-MM-DD') + ' ' + endTime,
      reservationItems,
      price: priceInfo?.price ?? null,
      currency: priceInfo?.currency ?? null,
    }
  }, [startDate, startTime, endDate, endTime, reservationItems, priceInfo])

  const handlePressBooking = useCallback(() => {
    if (priceInfo?.has_duplicated_reservations) {
      setDuplicateModalVisible(true)
      return
    }

    onBooking(buildBookingParams())
  }, [priceInfo, onBooking, buildBookingParams])

  const handleDuplicateConfirm = useCallback(() => {
    setDuplicateModalVisible(false)
    onBooking(buildBookingParams())
  }, [onBooking, buildBookingParams])

  const isBookingDisabled = !total || isLoading || isPriceLoading || !!timePickerError

  return (
    <div className={styles.container}>
      <SpaceBookingDatePicker
        startDate={startDate}
        endDate={endDate}
        startTime={startTime}
        endTime={endTime}
        timeRange={timeRange}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        space={space}
        timePickerError={timePickerError}
      />
      <SizePickingSection
        space={space}
        reservationItems={reservationItems}
        onPick={(size, count) => setReservationItems({ ...reservationItems, [size]: count })}
      />

      <PricingSection currencySymbol={currencySymbol} total={total} isPriceError={isPriceError} />

      <BookingActionSection
        isMobile={isMobile}
        isAuthorized={isAuthorized}
        isBookingDisabled={isBookingDisabled}
        onBooking={handlePressBooking}
        onRedirectLogin={handleRedirectLogin}
      />

      <DuplicateReserveModal
        isVisible={isDuplicateModalVisible}
        onClose={() => setDuplicateModalVisible(false)}
        onSubmit={handleDuplicateConfirm}
      />
    </div>
  )
}
